import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { BrandingProvider, useBranding } from "@/contexts/BrandingContext";
import { MasterAuthProvider } from "@/contexts/MasterAuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import "@/App.css";

// Marketing (DR AI FUTBOL platform site — shown at draifutbol.com)
import LandingPage from "@/pages/marketing/LandingPage";
import DemoRequestPage from "@/pages/marketing/DemoRequestPage";
import {
    FeaturesPage,
    AIFutbolPage,
    SocialMediaPage,
    WebSitePage,
    IletisimPage,
} from "@/pages/marketing/SecondaryPages";
import { AcademyPage as MarketingAcademyPage, PricingPage } from "@/pages/marketing/AcademyPricingPages";
import MarketingSEO from "@/components/marketing/MarketingSEO";

/** Wraps a marketing page with a per-route SEO header (title, description, OG tags). */
const SEOPage = ({ Page, title, description, path }) => (
    <>
        <MarketingSEO title={title} description={description} path={path} />
        <Page />
    </>
);

/** Renders <Platform /> when the request comes from the DR AI FUTBOL platform host
 *  (e.g. draifutbol.com or preview URL) and <Club /> when it's a registered club domain.
 *  Used by routes whose paths collide between the marketing site and club public site,
 *  e.g. /iletisim and /akademi. */
function PlatformOrClub({ Platform, Club, seoTitle, seoDescription, seoPath }) {
    const { branding, loaded } = useBranding();
    if (!loaded) return <div className="min-h-screen bg-dr-black" />;
    if (branding?.is_platform) {
        return (
            <>
                {seoTitle && <MarketingSEO title={seoTitle} description={seoDescription} path={seoPath} />}
                <Platform />
            </>
        );
    }
    return <Club />;
}

// Public pages
import HomeRouter from "@/pages/public/HomeRouter";
import Club from "@/pages/public/Club";
import Team from "@/pages/public/Team";
import Management from "@/pages/public/Management";
import Players from "@/pages/public/Players";
import PlayerDetail from "@/pages/public/PlayerDetail";
import TechnicalTeam from "@/pages/public/TechnicalTeam";
import MatchCenter from "@/pages/public/MatchCenter";
import Fixture from "@/pages/public/Fixture";
import Standings from "@/pages/public/Standings";
import News from "@/pages/public/News";
import NewsDetail from "@/pages/public/NewsDetail";
import Sponsors from "@/pages/public/Sponsors";
import Contact from "@/pages/public/Contact";
import Academy from "@/pages/public/Academy";
import AcademyAgeGroups from "@/pages/public/AcademyAgeGroups";
import AcademySchedule from "@/pages/public/AcademySchedule";
import AcademyTechnical from "@/pages/public/AcademyTechnical";
import AcademyNews from "@/pages/public/AcademyNews";
import AcademyApplication from "@/pages/public/AcademyApplication";

// Admin pages
import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminPlayers from "@/pages/admin/Players";
import AdminStaff from "@/pages/admin/Staff";
import AdminMatches from "@/pages/admin/Matches";
import AdminStandings from "@/pages/admin/Standings";
import AdminSponsors from "@/pages/admin/Sponsors";
import AdminOpponents from "@/pages/admin/Opponents";
import AdminPosts from "@/pages/admin/Posts";
import AdminAcademyGroups from "@/pages/admin/AcademyGroups";
import AdminAcademySessions from "@/pages/admin/AcademySessions";
import AdminApplications from "@/pages/admin/Applications";
import AdminSlides from "@/pages/admin/Slides";
import AdminMessages from "@/pages/admin/Messages";
import AdminMedia from "@/pages/admin/Media";
import AdminAi from "@/pages/admin/AiPanel";
import AdminAiStudio from "@/pages/admin/AiStudio";
import AdminTeamPhotos from "@/pages/admin/TeamPhotos";
import AdminSettings from "@/pages/admin/Settings";
import AdminUsers from "@/pages/admin/Users";
import AdminAccount from "@/pages/admin/Account";
import AdminMackolik from "@/pages/admin/MackolikSync";
import AdminPaketim from "@/pages/admin/Paketim";
import AdminTrainings from "@/pages/admin/Trainings";
import AdminAttendance from "@/pages/admin/Attendance";
import AdminFinance from "@/pages/admin/Finance";
import AdminContracts from "@/pages/admin/Contracts";
import AdminMatchAnalysis from "@/pages/admin/MatchAnalysis";
import AdminManagement from "@/pages/admin/Management";
import MasterMarketing from "@/pages/master/MasterMarketing";
import MasterLeads from "@/pages/master/MasterLeads";

// Master Admin
import MasterLogin from "@/pages/master/MasterLogin";
import MasterLayout from "@/components/master/MasterLayout";
import MasterDashboard from "@/pages/master/MasterDashboard";
import MasterClubs from "@/pages/master/MasterClubs";
import MasterClubDetail from "@/pages/master/MasterClubDetail";
import MasterOnboarding from "@/pages/master/MasterOnboarding";
import MasterPlans from "@/pages/master/MasterPlans";
import MasterSystem from "@/pages/master/MasterSystem";
import MasterAuditLog from "@/pages/master/MasterAuditLog";

function App() {
    return (
        <HelmetProvider>
            <BrandingProvider>
                <AuthProvider>
                    <MasterAuthProvider>
                        <BrowserRouter>
                        <ScrollToTop />
                        <Toaster position="top-right" richColors theme="dark" />
                        <Routes>
                            <Route path="/" element={<HomeRouter />} />

                            {/* DR AI FUTBOL marketing site (shown when host = draifutbol.com / preview) */}
                            <Route path="/ozellikler" element={<SEOPage Page={FeaturesPage} title="Özellikler" description="Sahaya, eğitime ve ofise eklediği değer — AI Antrenör, AI Sosyal Medya, Kurumsal Web Sitesi, Lisanslı Ödeme ve daha fazlası." path="/ozellikler" />} />
                            <Route path="/ai-futbol" element={<SEOPage Page={AIFutbolPage} title="AI Futbol" description="Yapay zeka destekli antrenman analizi, rakip raporu ve oyuncu istatistikleri — teknik direktörlerin asistanı." path="/ai-futbol" />} />
                            <Route path="/ai-sosyal-medya" element={<SEOPage Page={SocialMediaPage} title="AI Sosyal Medya" description="Maç sonrası caption + görsel üretimi tek tıkla. Instagram için 1080×1080, Story ve Reklam formatları otomatik." path="/ai-sosyal-medya" />} />
                            <Route path="/web-sitesi" element={<SEOPage Page={WebSitePage} title="Kurumsal Web Sitesi" description="5 farklı tema, tam responsive, otomatik lig verisi entegrasyonlu, SEO uyumlu kulüp web sitesi. Tek panelden yönetim." path="/web-sitesi" />} />
                            <Route path="/fiyatlandirma" element={<SEOPage Page={PricingPage} title="Fiyatlandırma" description="Starter, Plus, Pro — kulübünüzün ölçeğine göre tarife. AI kredi sistemi ve esnek yıllık plan seçenekleriyle." path="/fiyatlandirma" />} />
                            <Route path="/demo-talep" element={<SEOPage Page={DemoRequestPage} title="Ücretsiz Üye Ol" description="DR AI FUTBOL'a ücretsiz üye olun, kulübünüzü aynı gün yönetmeye başlayın." path="/uye-ol" />} />
                            <Route path="/uye-ol" element={<SEOPage Page={DemoRequestPage} title="Ücretsiz Üye Ol" description="DR AI FUTBOL'a ücretsiz üye olun, kulübünüzü aynı gün yönetmeye başlayın." path="/uye-ol" />} />
                            {/* Marketing /giris → club panel login (clubs log in on the platform host) */}
                            <Route path="/giris" element={<Navigate to="/admin" replace />} />

                            {/* Conflicting routes — render marketing on platform host, club page otherwise */}
                            <Route path="/iletisim" element={<PlatformOrClub Platform={IletisimPage} Club={Contact} seoTitle="İletişim" seoDescription="DR AI FUTBOL ekibiyle iletişime geçin. Sorularınız, üyelik talepleri ve iş birliği önerileri için bize ulaşın." seoPath="/iletisim" />} />
                            <Route path="/akademi" element={<PlatformOrClub Platform={MarketingAcademyPage} Club={Academy} seoTitle="Akademi Modülü" seoDescription="Altyapı yönetimi tek panelden — yaş grupları, antrenman takvimi, devam takibi ve veli iletişimi." seoPath="/akademi" />} />

                            {/* Club public pages */}
                            <Route path="/kulup" element={<Club />} />
                            <Route path="/yonetim" element={<Management />} />
                            <Route path="/takim" element={<Team />} />
                            <Route path="/oyuncular" element={<Players />} />
                            <Route path="/oyuncular/:slug" element={<PlayerDetail />} />
                            <Route path="/teknik-ekip" element={<TechnicalTeam />} />
                            <Route path="/mac-merkezi" element={<MatchCenter />} />
                            <Route path="/fikstur" element={<Fixture />} />
                            <Route path="/puan-durumu" element={<Standings />} />
                            <Route path="/haberler" element={<News />} />
                            <Route path="/haberler/:slug" element={<NewsDetail />} />
                            <Route path="/sponsorlar" element={<Sponsors />} />
                            <Route path="/akademi/yas-gruplari" element={<AcademyAgeGroups />} />
                            <Route path="/akademi/antrenman-takvimi" element={<AcademySchedule />} />
                            <Route path="/akademi/teknik-kadro" element={<AcademyTechnical />} />
                            <Route path="/akademi/haberler" element={<AcademyNews />} />
                            <Route path="/akademi/basvuru" element={<AcademyApplication />} />

                            {/* Admin */}
                            <Route path="/admin" element={<AdminLogin />} />
                            <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
                            <Route path="/admin/players" element={<AdminLayout><AdminPlayers /></AdminLayout>} />
                            <Route path="/admin/staff" element={<AdminLayout><AdminStaff /></AdminLayout>} />
                            <Route path="/admin/matches" element={<AdminLayout><AdminMatches /></AdminLayout>} />
                            <Route path="/admin/standings" element={<AdminLayout><AdminStandings /></AdminLayout>} />
                            <Route path="/admin/sponsors" element={<AdminLayout><AdminSponsors /></AdminLayout>} />
                            <Route path="/admin/opponents" element={<AdminLayout><AdminOpponents /></AdminLayout>} />
                            <Route path="/admin/posts" element={<AdminLayout><AdminPosts /></AdminLayout>} />
                            <Route path="/admin/academy-groups" element={<AdminLayout><AdminAcademyGroups /></AdminLayout>} />
                            <Route path="/admin/academy-sessions" element={<AdminLayout><AdminAcademySessions /></AdminLayout>} />
                            <Route path="/admin/applications" element={<AdminLayout><AdminApplications /></AdminLayout>} />
                            <Route path="/admin/slides" element={<AdminLayout><AdminSlides /></AdminLayout>} />
                            <Route path="/admin/messages" element={<AdminLayout><AdminMessages /></AdminLayout>} />
                            <Route path="/admin/media" element={<AdminLayout><AdminMedia /></AdminLayout>} />
                            <Route path="/admin/ai" element={<AdminLayout><AdminAi /></AdminLayout>} />
                            <Route path="/admin/ai-studio" element={<AdminLayout><AdminAiStudio /></AdminLayout>} />
                            <Route path="/admin/team-photos" element={<AdminLayout><AdminTeamPhotos /></AdminLayout>} />
                            <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
                            <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
                            <Route path="/admin/account" element={<AdminLayout><AdminAccount /></AdminLayout>} />
                            <Route path="/admin/mackolik" element={<AdminLayout><AdminMackolik /></AdminLayout>} />
                            <Route path="/admin/paketim" element={<AdminLayout><AdminPaketim /></AdminLayout>} />
                            <Route path="/admin/trainings" element={<AdminLayout><AdminTrainings /></AdminLayout>} />
                            <Route path="/admin/attendance" element={<AdminLayout><AdminAttendance /></AdminLayout>} />
                            <Route path="/admin/finance" element={<AdminLayout><AdminFinance /></AdminLayout>} />
                            <Route path="/admin/contracts" element={<AdminLayout><AdminContracts /></AdminLayout>} />
                            <Route path="/admin/match-analysis" element={<AdminLayout><AdminMatchAnalysis /></AdminLayout>} />
                            <Route path="/admin/management" element={<AdminLayout><AdminManagement /></AdminLayout>} />

                            {/* Master Admin */}
                            <Route path="/master" element={<MasterLogin />} />
                            <Route path="/master/login" element={<MasterLogin />} />
                            <Route path="/master/dashboard" element={<MasterLayout><MasterDashboard /></MasterLayout>} />
                            <Route path="/master/clubs" element={<MasterLayout><MasterClubs /></MasterLayout>} />
                            <Route path="/master/clubs/:clubId" element={<MasterLayout><MasterClubDetail /></MasterLayout>} />
                            <Route path="/master/onboarding" element={<MasterLayout><MasterOnboarding /></MasterLayout>} />
                            <Route path="/master/leads" element={<MasterLayout><MasterLeads /></MasterLayout>} />
                            <Route path="/master/marketing" element={<MasterLayout><MasterMarketing /></MasterLayout>} />
                            <Route path="/master/plans" element={<MasterLayout><MasterPlans /></MasterLayout>} />
                            <Route path="/master/audit-log" element={<MasterLayout><MasterAuditLog /></MasterLayout>} />
                            <Route path="/master/system" element={<MasterLayout><MasterSystem /></MasterLayout>} />
                        </Routes>
                    </BrowserRouter>
                </MasterAuthProvider>
            </AuthProvider>
        </BrandingProvider>
        </HelmetProvider>
    );
}

export default App;
